import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Gem, TrendingDown, TrendingUp } from 'lucide-react'
import { ResponsiveLine } from '@nivo/line'

const buildSeries = (myBets) => {
  const bets = [...myBets].reverse()
  let cumulative = 0
  const series = [{ x: 0, y: 0 }]

  bets.forEach((round, index) => {
    cumulative += Number(round?.profit || 0)
    series.push({ x: index + 1, y: cumulative })
  })

  return {
    series,
    totalNet: cumulative,
    wins: myBets.filter((round) => round?.is_winner).length,
    losses: myBets.filter((round) => !round?.is_winner).length
  }
}

const GamePerformanceChart = ({ myBets = [], mode, gameName }) => {
  const { t } = useTranslation()
  const performance = useMemo(() => buildSeries(Array.isArray(myBets) ? myBets : []), [myBets])

  if (!Array.isArray(myBets) || myBets.length === 0) return null

  const { series, totalNet, wins, losses } = performance
  const totalNetSign = totalNet > 0 ? '+' : totalNet < 0 ? '-' : ''
  const lineColor = totalNet >= 0 ? '#34d399' : '#fb7185'
  const lineAreaColor = totalNet >= 0 ? 'rgba(52,211,153,0.18)' : 'rgba(251,113,133,0.18)'
  const areaId = `${mode}PerformanceArea`

  return (
    <div className="rounded-2xl border border-white/10 bg-surface/50 p-4">
      <h3 className="text-sm font-semibold text-white">
        {t(`${mode}.performance.title`, 'Баланс моей игры')}
      </h3>

      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div className="rounded-lg border border-white/10 bg-dark-primary/40 p-2">
          <p className="text-text-secondary">{t(`${mode}.performance.total`, 'Итог')}</p>
          <p className={`mt-1 inline-flex items-center gap-1 font-semibold ${
            totalNet > 0
              ? 'text-emerald-300'
              : totalNet < 0
                ? 'text-rose-300'
                : 'text-white'
          }`}>
            <Gem className="h-3.5 w-3.5" />
            <span>{totalNetSign}{Math.abs(totalNet).toLocaleString()}</span>
          </p>
        </div>

        <div className="rounded-lg border border-white/10 bg-dark-primary/40 p-2">
          <p className="text-text-secondary">{t(`${mode}.performance.wins`, 'Победы')}</p>
          <p className="mt-1 inline-flex items-center gap-1 font-semibold text-emerald-300">
            <TrendingUp className="h-3.5 w-3.5" />
            <span>{wins}</span>
          </p>
        </div>

        <div className="rounded-lg border border-white/10 bg-dark-primary/40 p-2">
          <p className="text-text-secondary">{t(`${mode}.performance.losses`, 'Поражения')}</p>
          <p className="mt-1 inline-flex items-center gap-1 font-semibold text-rose-300">
            <TrendingDown className="h-3.5 w-3.5" />
            <span>{losses}</span>
          </p>
        </div>
      </div>

      <div className="mt-3 h-28 w-full rounded-xl border border-white/10 bg-dark-primary/40 p-2">
        <div className="h-full w-full" role="img" aria-label={t(`${mode}.performance.aria`, `График баланса игры в ${gameName}`)}>
          <ResponsiveLine
            data={[{ id: 'balance', data: series }]}
            margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
            xScale={{ type: 'point' }}
            yScale={{
              type: 'linear',
              min: 'auto',
              max: 'auto',
              stacked: false,
              reverse: false
            }}
            axisTop={null}
            axisRight={null}
            axisBottom={null}
            axisLeft={null}
            enableGridX={false}
            enableGridY={false}
            curve="monotoneX"
            colors={[lineColor]}
            lineWidth={3}
            enablePoints={false}
            animate={false}
            isInteractive={false}
            enableArea
            areaOpacity={1}
            areaBaselineValue={0}
            defs={[
              {
                id: areaId,
                type: 'linearGradient',
                colors: [
                  { offset: 0, color: lineAreaColor },
                  { offset: 100, color: 'rgba(0,0,0,0)' }
                ]
              }
            ]}
            fill={[{ match: '*', id: areaId }]}
            markers={[
              {
                axis: 'y',
                value: 0,
                lineStyle: {
                  stroke: 'rgba(148,163,184,0.45)',
                  strokeWidth: 1,
                  strokeDasharray: '4 4'
                }
              }
            ]}
          />
        </div>
      </div>
    </div>
  )
}

export default GamePerformanceChart
